import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { JsonInput } from '@shared/components/JsonInput';
import { getApiErrorMessage } from '@shared/utils/apiError.utils';
import type {
  CustomTranscriberRequestDto,
  TranscriberResponseDto,
} from '@shared/services/apiService/apiService';
import { useStyles } from '../styles';
import {
  buildCustomTranscriberPayload,
  parseParameters,
  type CustomTranscriberFields,
} from '../utils/customTranscriberForm';

type CustomTranscriberDialogProps = {
  open: boolean;
  transcriber?: TranscriberResponseDto;
  onClose: () => void;
  onSave: (transcriber: CustomTranscriberRequestDto, id?: string) => Promise<void>;
};

const emptyFields: CustomTranscriberFields = {
  provider: '',
  providerDisplayName: '',
  model: '',
  language: '',
  parameters: '',
};

const toFields = (transcriber?: TranscriberResponseDto): CustomTranscriberFields =>
  transcriber
    ? {
        provider: transcriber.provider,
        providerDisplayName: transcriber.providerDisplayName ?? '',
        model: transcriber.model ?? '',
        language: transcriber.language ?? '',
        parameters:
          transcriber.parameters && Object.keys(transcriber.parameters).length
            ? JSON.stringify(transcriber.parameters, null, 2)
            : '',
      }
    : emptyFields;

export const CustomTranscriberDialog: React.FC<CustomTranscriberDialogProps> = ({
  open,
  transcriber,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const [fields, setFields] = useState(() => toFields(transcriber));
  const [isProviderTouched, setIsProviderTouched] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const parsedParameters = useMemo(() => parseParameters(fields.parameters), [fields.parameters]);
  const normalizedProvider = fields.provider.trim();
  const isProviderValid = Boolean(normalizedProvider);
  const showProviderError = isProviderTouched && !isProviderValid;
  const canSave = isProviderValid && parsedParameters.valid;

  const updateField = (field: keyof CustomTranscriberFields, value: string) => {
    setFields((current) => ({ ...current, [field]: value }));
    setSaveError('');
  };

  const handleSave = async () => {
    if (isSaving || !canSave || !parsedParameters.valid) return;
    setIsSaving(true);
    setSaveError('');
    try {
      const payload = buildCustomTranscriberPayload(fields, parsedParameters.parameters);
      await onSave(payload, transcriber?.id);
      onClose();
    } catch (error: unknown) {
      setSaveError(getApiErrorMessage(error, t('transcriberPage.custom.saveFailed')));
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (!isSaving) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown={isSaving}
      fullWidth
      maxWidth="sm"
      aria-labelledby="custom-transcriber-title">
      <DialogTitle id="custom-transcriber-title">
        {transcriber
          ? t('transcriberPage.custom.editTitle')
          : t('transcriberPage.custom.createTitle')}
      </DialogTitle>
      <DialogContent dividers className={classes.dialogContent}>
        <Stack className={classes.formFields}>
          <TextField
            label={t('transcriberPage.form.providerLabel')}
            value={fields.provider}
            onChange={(event) => updateField('provider', event.target.value)}
            onBlur={() => setIsProviderTouched(true)}
            required
            disabled={isSaving}
            fullWidth
            autoFocus
            error={showProviderError}
            helperText={
              showProviderError ? t('transcriberPage.custom.providerRequired') : undefined
            }
          />
          <TextField
            label={t('transcriberPage.form.providerDisplayName')}
            value={fields.providerDisplayName}
            disabled={isSaving}
            onChange={(event) => updateField('providerDisplayName', event.target.value)}
            fullWidth
            helperText={t('transcriberPage.form.providerDisplayNameHelper', {
              provider: fields.provider || (t('transcriberPage.form.providerLabel') as string),
            })}
          />
          <TextField
            label={t('transcriberPage.custom.model')}
            value={fields.model}
            disabled={isSaving}
            onChange={(event) => updateField('model', event.target.value)}
            fullWidth
          />
          <TextField
            label={t('transcriberPage.form.languageLabel')}
            value={fields.language}
            disabled={isSaving}
            onChange={(event) => updateField('language', event.target.value)}
            fullWidth
            helperText={t('transcriberPage.custom.languageOptional')}
          />
          <JsonInput
            label={t('transcriberPage.custom.parameters')}
            value={fields.parameters}
            disabled={isSaving}
            onChange={(value) => updateField('parameters', value)}
            minRows={4}
            error={
              parsedParameters.valid ? undefined : t('transcriberPage.custom.parametersInvalid')
            }
            helperText={t('transcriberPage.custom.parametersHelper')}
            placeholder=""
          />
          {saveError && <Alert severity="error">{saveError}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSaving}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          disabled={!canSave || isSaving}
          aria-busy={isSaving}
          startIcon={
            isSaving ? <CircularProgress size={16} color="inherit" aria-hidden="true" /> : undefined
          }
          variant="contained">
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
